import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "@/lib/money";

import {
  ArrowLeft,
  Search,
  MapPin,
  BadgeCheck,
  User,
  Mail,
  Globe,
  FileText,
  ShieldCheck,
  Calendar,
  Languages,
  Stethoscope,
  ExternalLink,
  Download,
} from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { listTherapistApplications } from "@/lib/therapist-applications.functions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

export const Route = createFileRoute("/admin_/therapist-directory")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Therapist Directory — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminTherapistDirectory,
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-red-600">{(error as Error)?.message || "Error"}</div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm">Not found</div>,
});

type Row = {
  id: string;
  full_name: string;
  professional_title: string | null;
  email: string | null;
  website: string | null;
  country: string | null;
  state_region: string | null;
  licensing_jurisdiction: string | null;
  license_number: string | null;
  professional_body: string | null;
  qualifications: string | null;
  years_experience: number | null;
  therapy_approaches: string[] | null;
  specialisms: string[] | null;
  languages: string[] | null;
  availability_modes: string[] | null;
  short_bio: string | null;
  status: string;
  verified_badge: boolean | null;
  admin_notes: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  paid_at: string | null;
  licence_type: string | null;
  licence_expiry: string | null;
  regulatory_body: string | null;
  telehealth_permitted: boolean | null;
  insurance_confirmed: boolean | null;
  founding_member: boolean | null;
  payment_amount_cents: number | null;
  payment_currency: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  photo_signed_url: string | null;
  certificate_signed_url: string | null;
  insurance_certificate_signed_url: string | null;
};

const CSV_COLUMNS: Array<{ key: keyof Row; label: string }> = [
  { key: "full_name", label: "Full name" },
  { key: "professional_title", label: "Title" },
  { key: "email", label: "Email" },
  { key: "website", label: "Website" },
  { key: "country", label: "Country" },
  { key: "state_region", label: "State / Region" },
  { key: "licensing_jurisdiction", label: "Licensing jurisdiction" },
  { key: "license_number", label: "Licence number" },
  { key: "licence_type", label: "Licence type" },
  { key: "licence_expiry", label: "Licence expiry" },
  { key: "regulatory_body", label: "Regulatory body" },
  { key: "professional_body", label: "Professional body" },
  { key: "qualifications", label: "Qualifications" },
  { key: "years_experience", label: "Years experience" },
  { key: "therapy_approaches", label: "Approaches" },
  { key: "specialisms", label: "Specialisms" },
  { key: "languages", label: "Languages" },
  { key: "availability_modes", label: "Availability" },
  { key: "telehealth_permitted", label: "Telehealth permitted" },
  { key: "insurance_confirmed", label: "Insurance confirmed" },
  { key: "founding_member", label: "Founding member" },
  { key: "verified_badge", label: "Verified" },
  { key: "status", label: "Status" },
  { key: "short_bio", label: "Short bio" },
  { key: "submitted_at", label: "Submitted at" },
  { key: "reviewed_at", label: "Reviewed at" },
  { key: "approved_at", label: "Approved at" },
  { key: "published_at", label: "Published at" },
  { key: "paid_at", label: "Paid at" },
  { key: "payment_amount_cents", label: "Payment (cents)" },
  { key: "payment_currency", label: "Currency" },
  { key: "payment_method", label: "Payment method" },
  { key: "payment_reference", label: "Payment reference" },
  { key: "admin_notes", label: "Admin notes" },
  { key: "id", label: "Application ID" },
];

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  let str: string;
  if (Array.isArray(value)) str = value.join("; ");
  else if (typeof value === "boolean") str = value ? "yes" : "no";
  else str = String(value);
  if (/[",\n\r]/.test(str)) str = `"${str.replace(/"/g, '""')}"`;
  return str;
}

function exportProfilesToCsv(rows: Row[]) {
  if (rows.length === 0) return;
  const header = CSV_COLUMNS.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => CSV_COLUMNS.map((c) => csvEscape(r[c.key])).join(","))
    .join("\r\n");
  const csv = `\uFEFF${header}\r\n${body}\r\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `therapist-directory-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}



function AdminTherapistDirectory() {
  const fetcher = useServerFn(listTherapistApplications);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "therapist-directory"],
    queryFn: () => fetcher(),
  });
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState<"all" | "verified" | "unverified">("all");
  const [status, setStatus] = useState<string>("all");
  const [sort, setSort] = useState<"name_asc" | "name_desc" | "location_asc" | "location_desc">("name_asc");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const allRows = (data?.applications ?? []) as Row[];

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    allRows.forEach((r) => r.status && set.add(r.status));
    return Array.from(set).sort();
  }, [allRows]);

  const profiles = useMemo<Row[]>(() => {
    const term = q.trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      if (verified === "verified" && !r.verified_badge) return false;
      if (verified === "unverified" && r.verified_badge) return false;
      if (status !== "all" && r.status !== status) return false;
      if (!term) return true;
      return [r.full_name, r.professional_title, r.country, r.state_region]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
    const locOf = (r: Row) =>
      [r.country, r.state_region].filter(Boolean).join(", ").toLowerCase();
    const nameOf = (r: Row) => (r.full_name || "").toLowerCase();
    const cmp = (a: string, b: string) => a.localeCompare(b);
    const sorted = [...filtered].sort((a, b) => {
      switch (sort) {
        case "name_desc":
          return cmp(nameOf(b), nameOf(a));
        case "location_asc": {
          const la = locOf(a), lb = locOf(b);
          if (!la && lb) return 1;
          if (la && !lb) return -1;
          return cmp(la, lb) || cmp(nameOf(a), nameOf(b));
        }
        case "location_desc": {
          const la = locOf(a), lb = locOf(b);
          if (!la && lb) return 1;
          if (la && !lb) return -1;
          return cmp(lb, la) || cmp(nameOf(a), nameOf(b));
        }
        case "name_asc":
        default:
          return cmp(nameOf(a), nameOf(b));
      }
    });
    return sorted;
  }, [allRows, q, verified, status, sort]);

  const filtersActive = verified !== "all" || status !== "all" || q.length > 0;
  const totalPages = Math.max(1, Math.ceil(profiles.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = profiles.slice(pageStart, pageStart + pageSize);
  const selected = selectedId ? allRows.find((r) => r.id === selectedId) ?? null : null;

  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setPage(1);
  }, [q, verified, status, sort]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-10 sm:px-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>

      <header className="mt-4">
        <h1 className="font-display text-3xl text-foreground">Therapist Directory</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {profiles.length} {profiles.length === 1 ? "profile" : "profiles"}
          {profiles.length !== allRows.length ? ` of ${allRows.length}` : ""}
          {profiles.length > 0
            ? ` · showing ${pageStart + 1}–${pageStart + pageRows.length}`
            : ""}
          . Tap a row for full details.
        </p>
      </header>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_auto_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, title, or country…"
            className="w-full rounded-full border border-border bg-card py-2.5 ps-10 pe-4 text-sm outline-none focus:ring-2 focus:ring-gold/40"
          />
        </div>
        <select
          value={verified}
          onChange={(e) => setVerified(e.target.value as typeof verified)}
          aria-label="Filter by verification badge"
          className="rounded-full border border-border bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold/40"
        >
          <option value="all">All badges</option>
          <option value="verified">Verified only</option>
          <option value="unverified">Unverified only</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          className="rounded-full border border-border bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold/40"
        >
          <option value="all">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll("_", " ")}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sort profiles"
          className="rounded-full border border-border bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold/40"
        >
          <option value="name_asc">Name (A–Z)</option>
          <option value="name_desc">Name (Z–A)</option>
          <option value="location_asc">Location (A–Z)</option>
          <option value="location_desc">Location (Z–A)</option>
        </select>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {filtersActive && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setVerified("all");
              setStatus("all");
            }}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Clear filters
          </button>
        )}
        <button
          type="button"
          onClick={() => exportProfilesToCsv(profiles)}
          disabled={profiles.length === 0}
          className="ms-auto inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-medium text-foreground transition hover:bg-gold/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV ({profiles.length})
        </button>
      </div>


      {isLoading && <p className="mt-8 text-sm text-muted-foreground">Loading profiles…</p>}
      {error && (
        <p className="mt-8 text-sm text-red-600">
          {(error as Error)?.message || "Failed to load"}
        </p>
      )}

      {!isLoading && !error && (
        <ul className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
          {profiles.length === 0 && (
            <li className="p-6 text-sm text-muted-foreground">No profiles found.</li>
          )}
          {pageRows.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelectedId(p.id)}
                className="flex w-full items-start gap-4 p-4 text-start transition hover:bg-muted/40 focus:bg-muted/40 focus:outline-none sm:p-5"
              >
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
                  {p.photo_signed_url ? (
                    <img loading="lazy" decoding="async"
                      src={p.photo_signed_url}
                      alt={p.full_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <User className="h-6 w-6" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-medium text-foreground">{p.full_name}</p>
                    {p.verified_badge && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                        <BadgeCheck className="h-3 w-3" /> Verified
                      </span>
                    )}
                    <span className="ms-auto rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      {p.status.replaceAll("_", " ")}
                    </span>
                  </div>
                  {p.professional_title && (
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {p.professional_title}
                    </p>
                  )}
                  {(p.country || p.state_region) && (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {[p.state_region, p.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && profiles.length > pageSize && (
        <nav
          aria-label="Pagination"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm"
        >
          <p className="text-xs text-muted-foreground">
            Page {safePage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="rounded-full border border-border bg-card px-4 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="rounded-full border border-border bg-card px-4 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </nav>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          {selected && <ProfileDetail row={selected} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtMoney(cents: number | null, currency: string | null) {
  if (!cents) return "—";
  return formatMoney(cents, currency || "USD", { fromCents: true });
}


function ProfileDetail({ row }: { row: Row }) {
  return (
    <div className="space-y-6">
      <SheetHeader className="space-y-3 text-start">
        <div className="flex items-start gap-4">
          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
            {row.photo_signed_url ? (
              <img loading="lazy" decoding="async" src={row.photo_signed_url} alt={row.full_name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <User className="h-8 w-8" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-xl">{row.full_name}</SheetTitle>
            {row.professional_title && (
              <SheetDescription className="mt-0.5">{row.professional_title}</SheetDescription>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                {row.status.replaceAll("_", " ")}
              </span>
              {row.verified_badge && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              )}
              {row.founding_member && (
                <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-medium text-gold">
                  Founding Member
                </span>
              )}
              {row.telehealth_permitted && (
                <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300">
                  Telehealth
                </span>
              )}
            </div>
          </div>
        </div>
      </SheetHeader>

      <Section title="Contact">
        <Field icon={Mail} label="Email" value={row.email} copy />
        <Field
          icon={Globe}
          label="Website"
          value={row.website}
          link={row.website || undefined}
        />
        <Field
          icon={MapPin}
          label="Location"
          value={[row.state_region, row.country].filter(Boolean).join(", ") || null}
        />
      </Section>

      <Section title="Credentials">
        <Field icon={Stethoscope} label="Licence type" value={row.licence_type} />
        <Field icon={ShieldCheck} label="Regulatory body" value={row.regulatory_body || row.professional_body} />
        <Field label="Licence number" value={row.license_number} />
        <Field icon={Calendar} label="Licence expiry" value={row.licence_expiry} />
        <Field label="Licensing jurisdiction" value={row.licensing_jurisdiction} />
        <Field label="Qualifications" value={row.qualifications} />
        <Field label="Years experience" value={row.years_experience?.toString() ?? null} />
        <Field label="Insurance confirmed" value={row.insurance_confirmed ? "Yes" : "No"} />
      </Section>

      <Section title="Practice">
        <TagField label="Therapy approaches" values={row.therapy_approaches} />
        <TagField label="Specialisms" values={row.specialisms} />
        <TagField icon={Languages} label="Languages" values={row.languages} />
        <TagField label="Availability" values={row.availability_modes} />
      </Section>

      {row.short_bio && (
        <Section title="Bio">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{row.short_bio}</p>
        </Section>
      )}

      <Section title="Documents">
        <DocLink label="Profile photo" href={row.photo_signed_url} />
        <DocLink label="Qualification certificate" href={row.certificate_signed_url} />
        <DocLink label="Insurance certificate" href={row.insurance_certificate_signed_url} />
      </Section>

      <Section title="Payment">
        <Field label="Amount" value={fmtMoney(row.payment_amount_cents, row.payment_currency)} />
        <Field label="Method" value={row.payment_method} />
        <Field label="Reference" value={row.payment_reference} />
        <Field icon={Calendar} label="Paid at" value={fmtDate(row.paid_at)} />
      </Section>

      <Section title="Timeline">
        <Field icon={Calendar} label="Submitted" value={fmtDate(row.submitted_at)} />
        <Field icon={Calendar} label="Reviewed" value={fmtDate(row.reviewed_at)} />
        <Field icon={Calendar} label="Approved" value={fmtDate(row.approved_at)} />
        <Field icon={Calendar} label="Published" value={fmtDate(row.published_at)} />
      </Section>

      {row.admin_notes && (
        <Section title="Admin notes">
          <p className="whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-sm text-foreground/90">
            {row.admin_notes}
          </p>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="space-y-1.5 rounded-xl border border-border bg-card/50 p-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  icon: Icon,
  link,
  copy,
}: {
  label: string;
  value: string | null | undefined;
  icon?: React.ComponentType<{ className?: string }>;
  link?: string;
  copy?: boolean;
}) {
  const shown = value && String(value).trim() ? value : "—";
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="w-36 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words text-foreground">
        {link && value ? (
          <a
            href={link.startsWith("http") ? link : `https://${link}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
          >
            {shown} <ExternalLink className="h-3 w-3" />
          </a>
        ) : copy && value ? (
          <a href={`mailto:${value}`} className="underline-offset-2 hover:underline">
            {shown}
          </a>
        ) : (
          shown
        )}
      </span>
    </div>
  );
}

function TagField({
  label,
  values,
  icon: Icon,
}: {
  label: string;
  values: string[] | null | undefined;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="w-36 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
        {values && values.length > 0 ? (
          values.map((v) => (
            <span
              key={v}
              className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-foreground/80"
            >
              {v}
            </span>
          ))
        ) : (
          <span className="text-foreground">—</span>
        )}
      </div>
    </div>
  );
}

function DocLink({ label, href }: { label: string; href: string | null }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="inline-flex items-center gap-2 text-foreground">
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        {label}
      </span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-foreground underline-offset-2 hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-xs text-muted-foreground">Not provided</span>
      )}
    </div>
  );
}
